import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Checkbox,
  Input,
  Switch
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { ApiError, ProblemType } from '@/domains/api';
import type {
  ConcernCriterionKind,
  IConcernCriterion,
  IConcernCriterionBody
} from '../../api/work-item-contracts';
import { workItemsApi } from '../../api/work-items-api';
import { withUnknown } from '../../components/admin-page';
import {
  deriveCode,
  forgetMoved,
  newGroup,
  placeProblems,
  readPredicate,
  ruleWords,
  writePredicate,
  type IPlacedProblems,
  type IPredicateGroup,
  type IPredicateProblem
} from '../../concern-predicate';
import { refusalMessage } from '../../refusal';
import { WORK_ITEM_QUEUES, criterionKindLabel } from '../../work-items';
import { sentence, unanswered } from '../person-hub/said';
import { ConditionBlock, ProblemList } from './condition-group';
import { RuleWords, TechnicalDefinition } from './rule-words';

/**
 * A concern criterion's form — a new code's first version, or the next version of one (SR-222,
 * F9's R13; SR-227's builder, design `docs/superpowers/specs/2026-10-05-concern-criteria-builder-ui.md`
 * §3.3). Five steps in the order a person thinks about a rule — name, what to look at, conditions,
 * where the item goes, status — and above Save the rule read back in the reading view's own words.
 *
 * **Nothing is typed as JSON, and nothing is judged here.** The conditions are chosen — a field,
 * an operator, a value — and `writePredicate` composes the body from them; survey-api's parser
 * judges it and answers every problem at once, keyed by path, and each is drawn on the row or
 * group it names. The rejected alternative was the textarea this replaced, which only engineers
 * could use; the owner ruled the stored form is looked at, never edited (Q1), so the form's own
 * "Show the technical definition" shows what Save would send and offers nothing to type into.
 *
 * **A stored rule the mirror cannot read is kept, not rewritten** (R11): its conditions step is a
 * notice, Save sends the stored predicate verbatim, and "Replace the conditions" — confirmed — is
 * the one way to a blank builder. The rule is never lost, never mis-drawn, never re-typed. The kind
 * and the count are held with it (gate 2): the count is a member of the stored body, and the
 * conditions are one kind's shape, so a live box there would be a change saving silently drops.
 *
 * **The code is derived from the name** (Q3, ruled (a)) until "Change" opens its box: a code is an
 * engineer's idea, and survey-api judges it either way. The name's bound is said, never enforced.
 */

/** survey-api's bound on a criterion's name, said beside the field. */
const NAME_MAX = 200;

/** The kinds survey-api's parser takes — `ConcernCriterionKinds.All` — offered in this order. A
 *  stored kind outside them is offered too, as spelled, so an edit never changes it unasked. */
const KINDS: readonly ConcernCriterionKind[] = ['disclosure', 'aggregate'];

/** What each kind's card says under its name. */
const KIND_HINT: Record<string, string> = {
  disclosure: 'Raise an item for each disclosure that matches.',
  aggregate:
    'Raise an item when a person has at least this many live disclosures that match — live means on BrokerCheck today.'
};

/** The body members a refusal points at, as the form names them. */
const FIELD_LABEL: Record<string, string> = {
  code: 'Code',
  name: 'Name',
  kind: 'Kind',
  queues: 'Queues',
  enabled: 'Enabled'
};

function fieldLabel(target: string): string {
  if (/^queues\[\d+\]$/.test(target)) return 'Queues';
  return FIELD_LABEL[target] ?? target;
}

function isPredicatePath(target: string): boolean {
  return target === 'predicate' || target.startsWith('predicate.') || target.startsWith('predicate[');
}

/** The count's own path — said beside the "At least" box, not on the conditions. */
const COUNT_PATH = 'predicate.gte';

const NONE_PLACED: IPlacedProblems = { byNode: new Map(), unplaced: [] };

const STORED_DEFINITION_NOTE = 'What saving would send. It cannot be edited here.';

/** A criterion's 404 — the code the page holds names nothing now, and the list is read again. */
export function criterionGone(code: string): string {
  return `There is no criterion "${code}" any more — the criteria have been read again.`;
}

/** What a saved write answered — handed to the page, which says it and reads the list again. */
type CriterionSaved = (saved: IConcernCriterion) => void;

/** What the form hands back for a refusal that is about the page, not the fields: the list is
 *  read again and the sentence said above it. */
type CriterionStale = (text: string) => void;

/** The conditions an edit opens with: the builder's, or — where the mirror cannot read them — the
 *  stored body, kept. */
function startingConditions(initial: IConcernCriterion | undefined): {
  root: IPredicateGroup;
  atLeast: string;
  kept: { value: unknown } | null;
} {
  if (!initial) return { root: newGroup(), atLeast: '3', kept: null };
  const tree = readPredicate(initial.kind, initial.predicate);
  if (tree === null) {
    // The count is part of what is kept, so the box shows the stored one, as spelled.
    const stored = initial.predicate;
    const gte =
      typeof stored === 'object' && stored !== null && 'gte' in stored ? String(stored.gte) : '';
    return { root: newGroup(), atLeast: gte, kept: { value: initial.predicate } };
  }
  return {
    root: tree.root,
    atLeast: tree.atLeast === null ? '3' : String(tree.atLeast),
    kept: null
  };
}

export function CriterionForm({
  mode,
  initial,
  onSaved,
  onStale,
  onCancel,
  suggestions
}: {
  mode: 'create' | 'edit';
  /** The version an edit starts from — survey-api's current one, read afresh (B8). */
  initial?: IConcernCriterion;
  /** The values other criteria compare each Text member to — `usedValues` over the page's list. */
  suggestions: ReadonlyMap<string, readonly string[]>;
  onSaved: CriterionSaved;
  onStale: CriterionStale;
  onCancel: () => void;
}) {
  const [start] = React.useState(() => startingConditions(initial));
  const [name, setName] = React.useState(initial?.name ?? '');
  // `null` while the name drives the code; the box's text once "Change" opened it.
  const [ownCode, setOwnCode] = React.useState<Nullable<string>>(initial ? initial.code : null);
  const [kind, setKind] = React.useState<ConcernCriterionKind>(initial?.kind ?? 'disclosure');
  const [atLeast, setAtLeast] = React.useState(start.atLeast);
  const [root, setRoot] = React.useState<IPredicateGroup>(start.root);
  const [kept, setKept] = React.useState(start.kept);
  const [replacing, setReplacing] = React.useState(false);
  const [onlyThese, setOnlyThese] = React.useState(initial ? initial.queues !== null : false);
  const [queues, setQueues] = React.useState<string[]>(initial?.queues ?? []);
  const [enabled, setEnabled] = React.useState(initial?.enabled ?? true);
  const [problems, setProblems] = React.useState<IPredicateProblem[]>([]);
  // The conditions' problems, placed against the body survey-api refused (`forgetMoved`).
  const [placed, setPlaced] = React.useState<IPlacedProblems>(NONE_PLACED);
  const [codeTaken, setCodeTaken] = React.useState(false);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);
  const [busy, setBusy] = React.useState(false);

  const id = React.useId();
  const code = ownCode ?? deriveCode(name);
  const kinds = KINDS.includes(kind) ? KINDS : [...KINDS, kind];
  // Queues the mirror lacks, stored on the version an edit opens, stay ticked and offered.
  const offered = withUnknown(WORK_ITEM_QUEUES, queues);
  const subject = mode === 'edit' ? (initial?.name ?? code) : 'the new criterion';
  const tree = { root, atLeast };
  const written = writePredicate(kind, tree);
  const predicate = kept ? kept.value : written.body;

  const predicateProblems = problems.filter((problem) => isPredicatePath(problem.target));
  const fieldProblems = problems.filter((problem) => !isPredicatePath(problem.target));
  const countProblems = kept
    ? []
    : predicateProblems.filter((problem) => problem.target === COUNT_PATH);

  const changeRoot = (next: IPredicateGroup) => {
    setPlaced((held) => forgetMoved(held, root, next));
    setRoot(next);
  };

  const toggleQueue = (queue: string, on: boolean) =>
    setQueues((current) => (on ? [...current, queue] : current.filter((held) => held !== queue)));

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    setProblems([]);
    setPlaced(NONE_PLACED);
    setCodeTaken(false);
    setRefusal(null);
    const body: IConcernCriterionBody = {
      name,
      kind,
      predicate,
      // survey-api refuses `[]` and reads null as every queue, so none ticked is every queue.
      queues: onlyThese && queues.length > 0 ? queues : null,
      enabled
    };
    setBusy(true);
    try {
      const saved =
        mode === 'create'
          ? await workItemsApi.createConcernCriterion({ ...body, code })
          : await workItemsApi.reviseConcernCriterion(code, body);
      onSaved(saved);
    } catch (error) {
      setBusy(false);
      if (unanswered(error)) {
        onStale(
          `The answer did not arrive for ${subject} — the criteria have been read again: check ` +
            'whether it saved.'
        );
        return;
      }
      if (error instanceof ApiError && error.isProblem(ProblemType.codeInUse)) {
        if (mode === 'create') {
          setOwnCode(code);
          setCodeTaken(true);
          return;
        }
        // The form stands on a version that is no longer current, and survey-api takes no
        // `If-Match` here: a second press would land over the other save. So it closes.
        onStale(
          `Someone saved a version of ${subject} at the same moment — the criteria have been ` +
            'read again; nothing was saved. Edit it again from the version now current.'
        );
        return;
      }
      if (error instanceof ApiError && error.status === 404) {
        onStale(criterionGone(code));
        return;
      }
      const listed = error instanceof ApiError ? (error.problem?.problems ?? []) : [];
      if (listed.length > 0) {
        // A refused code is fixed in its box, so the box opens on what was sent.
        if (listed.some((problem) => problem.target === 'code')) setOwnCode(code);
        setProblems(listed);
        setPlaced(
          placeProblems(
            listed.filter((problem) => isPredicatePath(problem.target) && problem.target !== COUNT_PATH),
            tree,
            written.paths
          )
        );
        return;
      }
      setRefusal(sentence(refusalMessage(error, 'The criterion was not saved')));
    }
  };

  return (
    <form
      aria-label={mode === 'create' ? 'Add a criterion' : `Edit ${initial?.name ?? code}`}
      onSubmit={save}
      className="grid gap-5 text-[0.8125rem]"
    >
      <Step number={1} title="Name" titleFor={`${id}-name`}>
        <div className="grid max-w-xl gap-1">
          <Input id={`${id}-name`} value={name} onChange={(event) => setName(event.target.value)} />
          <span className="text-xs text-muted-foreground">
            {NAME_MAX} characters at most. An item names the version that raised it, so a new name
            does not rewrite old items.
          </span>
          {mode === 'edit' ? (
            <span>
              Code: <span className="font-mono">{code}</span>
              <span className="text-xs text-muted-foreground"> — it never changes once saved.</span>
            </span>
          ) : ownCode === null ? (
            <span className="flex flex-wrap items-center gap-1">
              Code:{' '}
              {code === '' ? (
                <span className="text-muted-foreground">made from the name</span>
              ) : (
                <span className="font-mono">{code}</span>
              )}
              <span aria-hidden>·</span>
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-auto p-0"
                onClick={() => setOwnCode(code)}
              >
                Change
              </Button>
            </span>
          ) : (
            <div className="grid gap-1">
              <label htmlFor={`${id}-code`} className="font-medium">
                Code
              </label>
              <Input
                id={`${id}-code`}
                value={ownCode}
                onChange={(event) => {
                  setOwnCode(event.target.value);
                  setCodeTaken(false);
                }}
                className="max-w-sm font-mono"
                autoComplete="off"
                spellCheck={false}
              />
              <span className="text-xs text-muted-foreground">
                Lowercase letters, digits and underscores; it never changes once saved.
              </span>
              {codeTaken && (
                <p role="alert" className="text-destructive">
                  A criterion with this code exists — edit it instead, or choose another code.
                </p>
              )}
            </div>
          )}
        </div>
      </Step>

      <Step number={2} title="What to look at">
        <fieldset aria-label="Kind" className="grid gap-2 sm:grid-cols-2">
          {kinds.map((option) => (
            <div
              key={option}
              className={
                'grid gap-2 rounded-md border p-3 ' +
                (kind === option
                  ? 'border-[var(--color-sapphire)] bg-[var(--color-off-white)]'
                  : 'border-border')
              }
            >
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  name={`${id}-kind`}
                  className="mt-0.5 accent-[var(--color-sapphire)]"
                  checked={kind === option}
                  disabled={kept !== null}
                  onChange={() => setKind(option)}
                />
                <span className="grid gap-0.5">
                  <span className="font-medium">{criterionKindLabel(option)}</span>
                  <span className="text-xs text-muted-foreground">
                    {KIND_HINT[option] ?? option}
                  </span>
                </span>
              </label>
              {option === 'aggregate' && (
                <div className="flex flex-wrap items-center gap-2 pl-6">
                  <label htmlFor={`${id}-count`}>At least</label>
                  <Input
                    id={`${id}-count`}
                    value={atLeast}
                    inputMode="numeric"
                    disabled={kind !== 'aggregate' || kept !== null}
                    onChange={(event) => setAtLeast(event.target.value)}
                    className="w-20"
                  />
                  <span>live disclosures</span>
                  {countProblems.map((problem, index) => (
                    <p key={index} role="alert" className="basis-full text-destructive">
                      {problem.reason}
                    </p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </fieldset>
      </Step>

      <Step number={3} title="Conditions">
        {kept ? (
          <div className="grid gap-2 rounded-md border border-border p-3">
            <p>
              This rule’s conditions are in a form this page cannot edit. They are kept exactly as
              they are, and so is what the rule looks at; the name, where the item goes and the
              status can still be changed.
            </p>
            {predicateProblems.length > 0 && (
              <div role="alert" className="grid gap-1 text-destructive">
                <p>Ask an engineer: this rule’s conditions were refused as stored.</p>
                <ProblemList problems={predicateProblems} />
              </div>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="w-fit"
              onClick={() => setReplacing(true)}
            >
              Replace the conditions
            </Button>
          </div>
        ) : (
          <ConditionBlock
            root={root}
            paths={written.paths}
            problemsOf={(nodeId) => placed.byNode.get(nodeId) ?? []}
            unplaced={placed.unplaced}
            suggestions={suggestions}
            onChange={changeRoot}
          />
        )}
      </Step>

      <Step number={4} title="Where the item goes">
        <fieldset aria-label="Where the item goes" className="grid gap-2">
          <label className="flex w-fit items-center gap-2">
            <input
              type="radio"
              name={`${id}-queues`}
              className="accent-[var(--color-sapphire)]"
              checked={!onlyThese}
              onChange={() => setOnlyThese(false)}
            />
            Every survey’s queue
          </label>
          <label className="flex w-fit items-center gap-2">
            <input
              type="radio"
              name={`${id}-queues`}
              className="accent-[var(--color-sapphire)]"
              checked={onlyThese}
              onChange={() => setOnlyThese(true)}
            />
            Only these queues
          </label>
          <div role="group" aria-label="Queues" className="flex flex-wrap gap-x-5 gap-y-2 pl-6">
            {offered.map((queue) => (
              <label key={queue} className="flex items-center gap-2 font-mono">
                <Checkbox
                  aria-label={queue}
                  disabled={!onlyThese}
                  checked={queues.includes(queue)}
                  onCheckedChange={(on) => toggleQueue(queue, on === true)}
                />
                {queue}
              </label>
            ))}
          </div>
          {onlyThese && queues.length === 0 && (
            <p className="pl-6 text-xs text-muted-foreground">
              None ticked — the item goes to every survey’s queue.
            </p>
          )}
        </fieldset>
      </Step>

      <Step number={5} title="Status">
        <label className="flex w-fit items-center gap-2">
          <Switch aria-label="Enabled" checked={enabled} onCheckedChange={setEnabled} />
          <span className="font-medium">Enabled</span>
        </label>
        <span className="text-xs text-muted-foreground">
          A disabled criterion raises no item; items it raised stay in their queues.
        </span>
      </Step>

      <div className="grid gap-2 rounded-md bg-[var(--color-off-white)] p-3">
        {kept ? (
          <p>This rule will keep its stored conditions.</p>
        ) : (
          <RuleWords words={ruleWords(kind, tree)} lead="form" />
        )}
        <TechnicalDefinition value={predicate} note={STORED_DEFINITION_NOTE} />
      </div>

      {fieldProblems.length > 0 && (
        <div role="alert">
          <ul aria-label="Refused" className="grid gap-1 text-destructive">
            {fieldProblems.map((problem, index) => (
              <li key={index}>
                {fieldLabel(problem.target)}: {problem.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
      {refusal && (
        <Alert variant="destructive">
          <AlertDescription>{refusal}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" size="sm" disabled={busy}>
          {busy ? 'Saving…' : mode === 'create' ? 'Add the criterion' : 'Save a new version'}
        </Button>
        <Button type="button" size="sm" variant="outline" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
        <span className="text-xs text-muted-foreground">
          {initial
            ? `Saves version ${initial.version + 1}. Items raised by v${initial.version} keep it.`
            : 'Saves version 1.'}
        </span>
      </div>

      <AlertDialog open={replacing} onOpenChange={setReplacing}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replace the conditions?</AlertDialogTitle>
            <AlertDialogDescription>
              The stored conditions will not be in the new version. The current version keeps them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setKept(null);
                setRoot(newGroup());
                setProblems([]);
                setPlaced(NONE_PLACED);
              }}
            >
              Replace the conditions
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  );
}

/** One of the form's five steps: its number, its title, its controls. */
function Step({
  number,
  title,
  titleFor,
  children
}: {
  number: number;
  title: string;
  /** The control the title labels, where the step is one control (the name). */
  titleFor?: string;
  children: React.ReactNode;
}) {
  const badge = (
    <span
      aria-hidden
      className="grid size-5 place-items-center rounded-full bg-[var(--color-midnight-blue)] text-[0.6875rem] text-white"
    >
      {number}
    </span>
  );
  return (
    <div className="grid gap-2">
      <div className="flex items-center gap-2 font-medium">
        {badge}
        {titleFor ? <label htmlFor={titleFor}>{title}</label> : <span>{title}</span>}
      </div>
      {children}
    </div>
  );
}
